import { Filter, Loader2, Wifi, Car, Users, Shield, Dumbbell, PawPrint, Coffee, Sparkles } from 'lucide-react'
import FilterSection from './FilterSection'
import DateRangePicker from '../roomDetails/booking/DateRangePicker'

interface FilterSidebarProps {
  filters: {
    checkIn: string
    checkOut: string
    location: string
    beds: string
    baths: string
    guests: string
    sortBy: string
    priceRange: [number, number]
    amenities: {
      wifi: boolean
      parking: boolean
      familyRooms: boolean
      nonSmoking: boolean
      gym: boolean
      petsAllowed: boolean
      teaCoffee: boolean
      roomCleaning: boolean
    }
  }
  expandedSections: {[key: string]: boolean}
  loading: boolean
  onFilterChange: (key: string, value: any) => void
  onAmenityChange: (amenity: string) => void
  onToggleSection: (section: string) => void
  onApplyFilters: () => void
  onResetFilters: () => void
  onOpenCalendar?: () => void
}

const amenitiesList = [
  { id: 'wifi', name: 'Free Wifi', icon: <Wifi className="h-4 w-4" /> },
  { id: 'parking', name: 'Parking', icon: <Car className="h-4 w-4" /> },
  { id: 'familyRooms', name: 'Family Rooms', icon: <Users className="h-4 w-4" /> },
  { id: 'nonSmoking', name: 'Non Smoking', icon: <Shield className="h-4 w-4" /> },
  { id: 'gym', name: 'Gym', icon: <Dumbbell className="h-4 w-4" /> },
  { id: 'petsAllowed', name: 'Pets Allowed', icon: <PawPrint className="h-4 w-4" /> },
  { id: 'teaCoffee', name: 'Tea/Coffee', icon: <Coffee className="h-4 w-4" /> },
  { id: 'roomCleaning', name: 'Room Cleaning', icon: <Sparkles className="h-4 w-4" /> }
]

const FilterSidebar = ({
  filters,
  expandedSections,
  loading,
  onFilterChange,
  onAmenityChange,
  onToggleSection,
  onApplyFilters,
  onResetFilters,
  onOpenCalendar
}: FilterSidebarProps) => {
  return (
    <div className="space-y-0">
      <FilterSection 
        title="Check In / Out" 
        sectionKey="dates"
        isExpanded={expandedSections.dates}
        onToggle={onToggleSection}
      >
        <button
          onClick={(e) => {
            e.stopPropagation()
            if (onOpenCalendar) {
              onOpenCalendar()
            }
          }}
          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-left hover:border-primary-500 transition-colors text-sm"
        >
          {filters.checkIn && filters.checkOut 
            ? `${new Date(filters.checkIn).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(filters.checkOut).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
            : 'Select dates'
          }
        </button>
      </FilterSection>

      <FilterSection 
        title="Location" 
        sectionKey="location"
        isExpanded={expandedSections.location}
        onToggle={onToggleSection}
      >
        <input
          type="text"
          placeholder="Enter location"
          value={filters.location}
          onChange={(e) => {
            e.stopPropagation()
            onFilterChange('location', e.target.value)
          }}
          onClick={(e) => e.stopPropagation()}
          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 text-sm"
        />
      </FilterSection>

      <FilterSection 
        title="Room Details" 
        sectionKey="rooms"
        isExpanded={expandedSections.rooms}
        onToggle={onToggleSection}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Beds</label>
            <select
              value={filters.beds}
              onChange={(e) => {
                e.stopPropagation()
                onFilterChange('beds', e.target.value)
              }}
              onClick={(e) => e.stopPropagation()}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 text-sm"
            >
              <option value="all">All</option>
              <option value="1">1 Bed</option>
              <option value="2">2 Beds</option>
              <option value="3">3 Beds</option>
              <option value="4+">4+ Beds</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Baths</label>
            <select
              value={filters.baths}
              onChange={(e) => {
                e.stopPropagation()
                onFilterChange('baths', e.target.value)
              }}
              onClick={(e) => e.stopPropagation()}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 text-sm"
            >
              <option value="all">All</option>
              <option value="1">1 Bath</option>
              <option value="2">2 Baths</option>
              <option value="3">3 Baths</option>
              <option value="4+">4+ Baths</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Guests</label>
            <select
              value={filters.guests}
              onChange={(e) => {
                e.stopPropagation()
                onFilterChange('guests', e.target.value)
              }}
              onClick={(e) => e.stopPropagation()}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 text-sm"
            >
              <option value="all">All</option>
              <option value="1">1 Guest</option>
              <option value="2">2 Guests</option>
              <option value="3">3 Guests</option>
              <option value="4">4 Guests</option>
              <option value="5+">5+ Guests</option>
            </select>
          </div>
        </div>
      </FilterSection>

      <FilterSection 
        title="Price Range" 
        sectionKey="price"
        isExpanded={expandedSections.price}
        onToggle={onToggleSection}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Min (₹)</label>
              <input
                type="number"
                min="500"
                max="10000"
                step="100"
                value={filters.priceRange[0]}
                onChange={(e) => {
                  e.stopPropagation()
                  onFilterChange('priceRange', [parseInt(e.target.value) || 500, filters.priceRange[1]])
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Max (₹)</label>
              <input
                type="number"
                min="500"
                max="10000"
                step="100"
                value={filters.priceRange[1]}
                onChange={(e) => {
                  e.stopPropagation()
                  onFilterChange('priceRange', [filters.priceRange[0], parseInt(e.target.value) || 5000])
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 text-sm"
              />
            </div>
          </div>
          <div>
            <input
              type="range"
              min="500"
              max="10000"
              step="100"
              value={filters.priceRange[1]}
              onChange={(e) => {
                e.stopPropagation()
                onFilterChange('priceRange', [filters.priceRange[0], parseInt(e.target.value)])
              }}
              onClick={(e) => e.stopPropagation()}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex items-center justify-between text-xs text-gray-600 mt-1">
              <span>₹{filters.priceRange[0]}</span>
              <span>₹{filters.priceRange[1]}</span>
            </div>
          </div>
        </div>
      </FilterSection>

      <FilterSection 
        title="Amenities" 
        sectionKey="amenities"
        isExpanded={expandedSections.amenities}
        onToggle={onToggleSection}
      >
        <div className="grid grid-cols-1 gap-3">
          {amenitiesList.map((amenity) => (
            <label 
              key={amenity.id} 
              className="flex items-center gap-3 cursor-pointer group"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={filters.amenities[amenity.id as keyof typeof filters.amenities]}
                onChange={(e) => {
                  e.stopPropagation()
                  onAmenityChange(amenity.id)
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-4 h-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500 cursor-pointer"
              />
              <div className="flex items-center gap-2 flex-1">
                <div className="text-gray-500 group-hover:text-primary-500 transition-colors">
                  {amenity.icon}
                </div>
                <span className="text-sm text-gray-700">{amenity.name}</span>
              </div>
            </label>
          ))}
        </div>
      </FilterSection>

      <div className="pt-4 space-y-2">
        <button 
          onClick={(e) => {
            e.stopPropagation()
            onApplyFilters()
          }}
          disabled={loading}
          className="w-full bg-primary-500 text-white py-3 rounded-lg font-semibold hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Applying...
            </>
          ) : (
            <>
              <Filter className="h-4 w-4" />
              Apply Filters
            </>
          )}
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation()
            onResetFilters()
          }}
          className="w-full bg-gray-100 text-gray-700 py-2.5 rounded-lg font-medium hover:bg-gray-200 transition-colors text-sm"
        >
          Reset Filters
        </button>
      </div>
    </div>
  )
}

export default FilterSidebar

